import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IdCard,
  Phone,
  FileText,
  Briefcase,
  NotebookPen,
  Camera,
  HeartPulse,
  Plus,
  Trash2,
  Upload,
  Check,
  CheckCircle2,
  ShieldCheck,
  Lock,
  User,
  UserCog,
  UserRound,
  Users,
  Copy,
  Save,
  X,
  MapPin,
  Wallet,
  GraduationCap,
  Home,
  Contact,
} from "lucide-react";
import PassportDocumentViewer from "../agent/PassportDocumentViewer.jsx";
import { copyTextToClipboard } from "../agent/clipboard.js";
import "../agent/applicantCard.css";
import { useWorkerDetailedProfile } from "./useWorkerDetailedProfile.js";
import {
  saveWorkerDetailedProfile,
  uploadWorkerPassportDocument,
  uploadWorkerPhoto,
  uploadWorkerExtraDocument,
  deleteWorkerExtraDocument,
} from "./workerDetailedProfileApi.js";
import StoredFileImage from "./StoredFileImage.jsx";
import WorkSamples from "./WorkSamples.jsx";
import "./workerDetailedProfile.css";

import { DetailSkeleton } from "../components/Skeleton.jsx";
/**
 * P146 - Detailed Worker Profile editor for a Quick Registered worker.
 *
 * A worker who registered through Quick Register is already a real member of
 * an Agent's line; e-KYC and Ready for Match are separate, later facts about
 * how complete/verified their record is. This editor is where an Agent or
 * backoffice officer fills in everything Quick Register deliberately did not
 * ask for, so the worker can actually be matched to a job later.
 *
 * Three things this screen never does:
 *  - never implies e-KYC passed or the worker is match-ready. Both pills are
 *    rendered from the backend's own `statuses` block, which is derived from
 *    a real promoted dbo.WmxWorkers row and nothing else. Filling in every
 *    field turns the completeness meter green and changes neither pill.
 *  - never shows a storage key, file path, or raw passport number. The API
 *    returns only a masked number and safe file metadata.
 *  - never decides for itself who may edit. `canEdit` comes from the backend,
 *    and the backend independently refuses an out-of-scope write regardless
 *    of what this component renders.
 */

const SECTIONS = [
  { key: "Identity", label: "ข้อมูลตัวตน", Icon: IdCard },
  { key: "Contact", label: "ข้อมูลติดต่อ", Icon: Phone },
  { key: "Documents", label: "เอกสารประจำตัว", Icon: FileText },
  { key: "Health", label: "สุขภาพ", Icon: HeartPulse },
  { key: "Skills", label: "ทักษะและการจับคู่งาน", Icon: Briefcase },
  { key: "Notes", label: "บันทึกเจ้าหน้าที่", Icon: NotebookPen },
];

/**
 * P147: the five repeating sections, described once as data so the add/remove
 * plumbing, the empty row, and the payload assembly cannot drift apart. Each
 * `requiredKey` names the one field that decides whether a row is real: a row
 * with only that field blank is dropped silently (an abandoned "add row"), and
 * the backend applies exactly the same rule.
 */
const REPEATING_SECTIONS = [
  { key: "languages", label: "ทักษะภาษา", addLabel: "เพิ่มภาษา", requiredKey: "languageName" },
  { key: "skillItems", label: "ทักษะและความสามารถ", addLabel: "เพิ่มทักษะ", requiredKey: "skillName" },
  { key: "educations", label: "ประวัติการศึกษา", addLabel: "เพิ่มการศึกษา", requiredKey: "institutionName" },
  { key: "experiences", label: "ประสบการณ์ทำงาน", addLabel: "เพิ่มประสบการณ์", requiredKey: "companyName" },
  { key: "visas", label: "วีซ่าและใบอนุญาตทำงาน", addLabel: "เพิ่มวีซ่า", requiredKey: "visaType" },
];

const EMPTY_ROWS = Object.freeze({
  languages: { languageName: "", listeningLevel: "", speakingLevel: "", readingLevel: "", writingLevel: "" },
  skillItems: { skillName: "", experienceYears: "" },
  educations: { institutionName: "", educationLevelText: "", major: "", startYear: "", endYear: "", gpaText: "" },
  experiences: { companyName: "", jobTitle: "", countryName: "", startYear: "", endYear: "", salaryText: "", remark: "" },
  visas: { visaType: "", issuingCountry: "", visaNumber: "", issueDate: "", expiryDate: "" },
});

/** Yes / no / not-asked. Null is a real third answer, never folded into "no". */
const YES_NO_OPTIONS = [
  { value: "", label: "ยังไม่ได้ถาม" },
  { value: "false", label: "ไม่" },
  { value: "true", label: "ใช่" },
];

function toFlagValue(flag) {
  if (flag === true) {
    return "true";
  }
  if (flag === false) {
    return "false";
  }
  return "";
}

function fromFlagValue(value) {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return null;
}

const EMPTY_FORM = Object.freeze({
  fullNameNative: "",
  fullNameEnglish: "",
  localName: "",
  genderCode: "",
  dateOfBirth: "",
  nationality: "",
  phoneNumber: "",
  whatsAppNumber: "",
  preferredLanguageCode: "",
  homeCountryAddress: "",
  passportNumber: "",
  passportExpiryDate: "",
  passportIssuingCountry: "",
  primarySkillCode: "",
  primarySkillOtherText: "",
  yearsOfExperience: "",
  secondarySkills: "",
  spokenLanguages: "",
  certifications: "",
  desiredJobTypes: "",
  expectedSalaryAmount: "",
  expectedSalaryCurrency: "",
  expectedSalaryPeriod: "",
  preferredProvinces: "",
  availableFromDate: "",
  backofficeNotes: "",
  // --- P147 flat fields ---
  titlePrefix: "",
  firstName: "",
  lastName: "",
  firstNameEn: "",
  lastNameEn: "",
  citizenNo: "",
  email: "",
  currentAddress: "",
  currentProvince: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelation: "",
  bloodType: "",
  medicalCondition: "",
  smokingFlag: "",
  drinkingFlag: "",
  covidVaccinationFlag: "",
  educationLevelSummary: "",
});

const OTHER_SKILL_CODE = "Other";

export function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Maps the API response's `detail` block onto controlled-input state. Every
 * null becomes "" so React never flips an input between uncontrolled and
 * controlled; `workerDetailedProfileApi.buildSaveBody` turns "" back into
 * null on the way out, so an emptied field really does clear the stored value.
 *
 * passportNumber is deliberately NOT seeded from the masked value - putting
 * "*****4512" in the box would submit those asterisks back as a real passport
 * number. The box starts empty with the masked value shown beside it, and an
 * untouched box means "leave the stored number alone".
 */
export function toFormState(profile) {
  const detail = profile?.detail ?? {};
  const next = { ...EMPTY_FORM };
  for (const key of Object.keys(EMPTY_FORM)) {
    if (key === "passportNumber" || key === "primarySkillOtherText") {
      continue;
    }
    // P147: the three health flags are tri-state (yes / no / not asked), so
    // they map through toFlagValue - String(false) would render as "false" and
    // then read back as a filled answer.
    if (key === "smokingFlag" || key === "drinkingFlag" || key === "covidVaccinationFlag") {
      next[key] = toFlagValue(detail[key]);
      continue;
    }
    const value = detail[key];
    next[key] = value === null || value === undefined ? "" : String(value);
  }
  // "Other" is the one primary-skill code whose display name is real
  // free text rather than a fixed table entry, so it round-trips into
  // its own companion input.
  if (detail.primarySkillCode === OTHER_SKILL_CODE) {
    next.primarySkillOtherText = detail.primarySkillName ?? "";
  }
  return next;
}

/**
 * P147: seeds the five repeating sections from a profile response. Every value
 * becomes a string because these feed controlled inputs; numbers go back to
 * numbers in the payload builder.
 */
export function toRowsState(profile) {
  const sections = profile?.sections ?? {};
  const next = {};
  for (const { key } of REPEATING_SECTIONS) {
    const rows = Array.isArray(sections[key]) ? sections[key] : [];
    next[key] = rows.map((row) => {
      const shaped = { ...EMPTY_ROWS[key] };
      for (const field of Object.keys(shaped)) {
        const value = row[field];
        shaped[field] = value === null || value === undefined ? "" : String(value);
      }
      return shaped;
    });
  }
  return next;
}

function toOptionalInt(value) {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function toOptionalText(value) {
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * P147: one repeating section - a titled card with an "add" button and a
 * removable row per entry.
 *
 * Rows are keyed by index deliberately. These rows have no server id until
 * they are saved (the backend replaces the whole section), so index is the only
 * stable identity available, and rows are never reordered by this component -
 * only appended and removed, which is the case where index keys behave.
 */
function RepeatingSection({ name, title, addLabel, rows, canEdit, onAdd, onRemove, emptyText, missing, children }) {
  return (
    <div className="wx-wdp-repeat" data-field={name}>
      <div className="wx-wdp-repeat-head">
        <h4 className="wx-wdp-group wx-wdp-group--repeat">
          {title}
          {rows.length > 0 && <span className="wx-wdp-count">{rows.length}</span>}
        </h4>
        {canEdit && (
          <button type="button" className="wx-button wx-button--ghost wx-wdp-repeat-add" onClick={onAdd}>
            <Plus size={14} aria-hidden="true" />
            {addLabel}
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className={`wx-wdp-repeat-empty${missing ? " wx-wdp-repeat-empty--missing" : ""}`}>
          {emptyText}
          {missing && <span className="wx-wdp-field-flag">ยังขาด</span>}
        </p>
      ) : (
        <ul className="wx-wdp-repeat-list">
          {rows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index} className="wx-wdp-repeat-row">
              <div className="wx-wdp-repeat-fields">{children(row, index)}</div>
              {canEdit && (
                <button
                  type="button"
                  className="wx-wdp-repeat-remove"
                  onClick={() => onRemove(index)}
                  aria-label={`ลบ${title}รายการที่ ${index + 1}`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function describeLoadError(error) {
  if (error?.status === 404) {
    return "ไม่พบข้อมูลแรงงานรายนี้";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงข้อมูลแรงงานรายนี้";
  }
  return "ไม่สามารถโหลดข้อมูลโปรไฟล์ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeSaveError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์แก้ไขข้อมูลแรงงานรายนี้";
  }
  if (error?.status === 400) {
    return "ข้อมูลบางช่องยังไม่ถูกต้อง กรุณาตรวจสอบแล้วบันทึกอีกครั้ง";
  }
  if (error?.status === 404) {
    return "ไม่พบข้อมูลแรงงานรายนี้แล้ว กรุณาปิดหน้าต่างนี้แล้วเปิดใหม่";
  }
  return "บันทึกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
}

function formatFileSize(sizeBytes) {
  if (!sizeBytes && sizeBytes !== 0) {
    return null;
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} ไบต์`;
  }
  const kilobytes = sizeBytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(0)} กิโลไบต์`;
  }
  return `${(kilobytes / 1024).toFixed(1)} เมกะไบต์`;
}

/**
 * The three status readouts the packet requires to stay visually distinct:
 * what Quick Register captured, whether e-KYC actually passed, and whether the
 * worker is actually match-ready. Deliberately three separate pills rather
 * than one blended "status" - blending them is exactly how a half-filled
 * record starts looking verified.
 */
/**
 * P157-R6: each pill carries the icon of the thing it reports, and takes its
 * colour from the real backend flag - green only when the backend actually says
 * approved / ready, never from anything this component computes.
 */
function StatusPill({ Icon, tone, children }) {
  return (
    <span className={`wx-wdp-pill wx-wdp-pill--${tone}`}>
      <Icon size={15} aria-hidden="true" />
      {children}
    </span>
  );
}

function StatusPills({ statuses, completeness }) {
  return (
    <div className="wx-wdp-pills">
      <StatusPill Icon={User} tone="neutral">
        สมัครผ่าน Quick Register
      </StatusPill>
      <StatusPill Icon={CheckCircle2} tone={statuses?.ekycApproved ? "ok" : "pending"}>
        {statuses?.ekycStatusLabel ?? "ยังไม่ผ่าน e-KYC"}
      </StatusPill>
      <StatusPill Icon={Users} tone={statuses?.readyForMatch ? "ok" : "pending"}>
        {statuses?.readyForMatchLabel ?? "ยังไม่พร้อมจับคู่งาน"}
      </StatusPill>
      <StatusPill Icon={ShieldCheck} tone={completeness?.isComplete ? "ok" : "info"}>
        {completeness?.levelLabel ?? "ยังไม่ได้กรอกข้อมูลเพิ่มเติม"}
      </StatusPill>
    </div>
  );
}

/**
 * The one number this whole dialog exists to move. It now sits in the pinned
 * header band rather than in the scroll flow, so it stays answerable ("how much
 * is left?") for the entire editing session instead of scrolling away after the
 * first field.
 */
function CompletenessMeter({ completeness }) {
  const percent = Math.max(0, Math.min(100, completeness?.percent ?? 0));
  const complete = completeness?.isComplete === true;
  return (
    <div className={`wx-wdp-meter${complete ? " wx-wdp-meter--complete" : ""}`}>
      <div className="wx-wdp-meter-head">
        <span className="wx-wdp-meter-caption">ระดับความพร้อมของข้อมูล</span>
        <strong className="wx-wdp-meter-value">
          {completeness?.filledCount ?? 0}/{completeness?.totalCount ?? 0} ช่อง ({percent}%)
        </strong>
      </div>
      <div
        className="wx-wdp-meter-track"
        role="progressbar"
        aria-label="ระดับความพร้อมของข้อมูล"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`wx-wdp-meter-fill ${complete ? "wx-wdp-meter-fill--complete" : ""}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

/**
 * The missing-field checklist. Each entry names a section the editor really
 * renders, so clicking one lands on a control that can actually fix it - a
 * checklist that points nowhere is worse than none.
 *
 * P157-R4: demoted from a full amber warning card to one quiet strip. Sixteen
 * amber chips on a normal half-filled record read as sixteen alarms, which is
 * exactly the "alarm chrome" the product's own calm-under-pressure principle
 * rules out - and an unfinished profile is a normal state of a real worker, not
 * an error. The chips now carry which TAB each item lives on, so the strip is a
 * map rather than a list of complaints, and the same information is repeated
 * where it is actually actionable: as a count on every tab and as a marker on
 * the individual empty field.
 */
/**
 * P157-R6: the chip strip is gone entirely, at the owner's direction ("do not
 * show the giant missing-field chip soup"). Nothing it carried was lost - the
 * same server-supplied `missingFields` now drives three quieter signals that sit
 * where the work is: the red asterisk on each field's own label, the per-tab
 * count, and the meter. This component survives only for the all-done case,
 * which is a single confirming line rather than an absence.
 */
function CompleteNotice({ completeness }) {
  if ((completeness?.missingFields ?? []).length > 0) {
    return null;
  }
  return (
    <p className="wx-wdp-complete-note">
      <Check size={15} aria-hidden="true" />
      กรอกข้อมูลสำหรับจับคู่งานครบทุกช่องแล้ว
    </p>
  );
}

/**
 * P157-R7: the Quick Register panel is gone at the owner's request ("ให้ซ่อนไปได้
 * เลยครับ ... มันเปลืองที่ครับ"). It repeated above all six tabs and, on this
 * record, six of its seven values already sat in the form below it: the backend
 * seeds `detail` from the same application, so สัญชาติ/เบอร์โทร/ภาษา/ความถนัด
 * are editable fields, and the masked passport number is already the hint on the
 * passport field.
 *
 * WHAT THIS COSTS, stated plainly: P146 required the worker's own submission to
 * stay visually separate from what staff typed afterwards, so an operator could
 * always tell the two apart. That distinction is no longer on screen. The one
 * value that lived nowhere else, สายงาน (agent / PLC / super agent), moved into
 * the header line rather than being dropped.
 */

/**
 * The hint deliberately sits OUTSIDE the <label> element. Nesting it inside
 * folds the hint text into the control's accessible name ("ทักษะรอง คั่นแต่ละ
 * รายการด้วยเครื่องหมายจุลภาค"), which is both wrong for a screen reader and
 * makes the field unfindable by its actual label.
 *
 * P157-R4: `missing` marks a field the backend's own completeness check counts
 * as still empty. The note sits outside the <label> for the same reason the
 * hint does - folded in, it would rename the control to "เพศ ยังขาด" and make
 * it unfindable by its real label. Tint plus that note, never tint alone: the
 * product's status rule is colour AND a text label, always both.
 */
/**
 * P157-R5: a labelled break inside a panel's field grid. One vocabulary for
 * every sub-group on every tab, replacing the two that had grown up separately
 * (an h4 "subhead" on Contact, a differently-styled title on each repeating
 * block). It is a grid item that spans the full row, so the fields under it stay
 * on the same columns as the fields above it, which a separate wrapper element
 * would have broken.
 */
/**
 * P157-R7: "จังหวัด/พื้นที่ที่ทำงานได้" used to be a textarea in which an operator
 * typed Thai province names by hand, comma separated, while the backend was
 * already serving all 77 real provinces in `options.provinces` - the same list
 * the single-province field beside it has always used. Typing them meant every
 * spelling mistake became stored data that no province filter would ever match.
 *
 * Pick from the list, remove with the chip. The stored value is still the exact
 * same comma-separated string the API has always taken, so nothing about the
 * payload changed; only the way the operator produces it did.
 */
/**
 * P157-R7: a year is a bounded list, so it is a dropdown rather than a spinner
 * an operator can type 20255 into. The range is computed from the browser clock,
 * not a hardcoded table, so it never goes stale: back to 1950 for a birth-era
 * education record, forward five years for a course that has not finished.
 *
 * A value already stored outside that window (an old record, a typo saved years
 * ago) is still offered as its own option, so opening this profile can never
 * silently rewrite it to blank on the next save.
 */
function YearSelect({ value, onChange, disabled }) {
  const thisYear = new Date().getFullYear();
  const years = [];
  for (let year = thisYear + 5; year >= 1950; year -= 1) {
    years.push(year);
  }
  const current = String(value ?? "");
  const known = current === "" || years.some((year) => String(year) === current);
  return (
    <select value={current} onChange={onChange} disabled={disabled}>
      <option value="">ยังไม่ระบุ</option>
      {!known && <option value={current}>{current} (ค่าเดิมในระบบ)</option>}
      {years.map((year) => (
        <option key={year} value={year}>
          {year}
        </option>
      ))}
    </select>
  );
}

function ProvincePicker({ value, options, disabled, onChange }) {
  const selected = String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const remaining = (options ?? []).filter((option) => !selected.includes(option.code));

  function add(code) {
    if (!code || selected.includes(code)) {
      return;
    }
    onChange([...selected, code].join(", "));
  }

  function remove(code) {
    onChange(selected.filter((item) => item !== code).join(", "));
  }

  return (
    <div className="wx-wdp-picker">
      <select
        className="wx-wdp-picker-select"
        value=""
        disabled={disabled}
        aria-label="เพิ่มจังหวัดที่ทำงานได้"
        onChange={(event) => {
          add(event.target.value);
          // Snap back to the prompt so the control always reads "add another"
          // rather than showing the province that was just added.
          event.target.value = "";
        }}
      >
        <option value="">เลือกจังหวัดเพื่อเพิ่ม...</option>
        {remaining.map((option) => (
          <option key={option.code} value={option.code}>
            {option.label}
          </option>
        ))}
      </select>
      {selected.length > 0 && (
        <ul className="wx-wdp-picker-chips">
          {selected.map((code) => (
            <li key={code}>
              <span className="wx-wdp-chip">
                {code}
                {!disabled && (
                  <button type="button" onClick={() => remove(code)} aria-label={`เอา ${code} ออก`}>
                    <X size={13} aria-hidden="true" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * P157-R9: documents beyond the single passport slot - add, preview, remove.
 *
 * Every row here is a real dbo.WmxWorkerApplicationDocuments row; there is no
 * client-side list. Deleting soft-deletes on the backend, so a document removed
 * by a mis-click stays recoverable in the table rather than being gone with its
 * bytes orphaned on disk.
 */
function ExtraDocuments({ applicationId, documents, canEdit, onChanged }) {
  const inputRef = useRef(null);
  const [documentType, setDocumentType] = useState("");
  const [state, setState] = useState({ status: "idle", message: null });
  const [busyId, setBusyId] = useState(null);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (!file) {
      return;
    }
    setState({ status: "uploading", message: null });
    try {
      const updated = await uploadWorkerExtraDocument(applicationId, file, documentType.trim());
      onChanged?.(updated);
      setDocumentType("");
      setState({ status: "idle", message: null });
    } catch (error) {
      setState({
        status: "error",
        message: error?.body?.message ?? "อัปโหลดเอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      });
    }
  }

  async function handleDelete(documentId) {
    setBusyId(documentId);
    setState({ status: "idle", message: null });
    try {
      const updated = await deleteWorkerExtraDocument(applicationId, documentId);
      onChanged?.(updated);
    } catch (error) {
      setState({
        status: "error",
        message: error?.body?.message ?? "ลบเอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      });
    } finally {
      setBusyId(null);
    }
  }

  const rows = documents ?? [];

  return (
    <div className="wx-wdp-extra">
      <h4 className="wx-wdp-group">
        <FileText size={16} aria-hidden="true" />
        <span>เอกสารเพิ่มเติม</span>
        {rows.length > 0 && <span className="wx-wdp-count">{rows.length}</span>}
      </h4>

      {rows.length === 0 ? (
        <p className="wx-wdp-repeat-empty">ยังไม่มีเอกสารเพิ่มเติม</p>
      ) : (
        <ul className="wx-wdp-extra-list">
          {rows.map((document) => (
            <li key={document.documentId} className="wx-wdp-extra-row">
              <StoredFileImage
                path={`/backoffice/worker-applications/${encodeURIComponent(applicationId)}/detailed-profile/documents/${encodeURIComponent(document.documentId)}/file`}
                label={document.documentType || document.fileName || "เอกสาร"}
                present
              />
              <div className="wx-wdp-extra-meta">
                <strong>{document.documentType || "เอกสารแนบ"}</strong>
                <span>
                  {document.fileName || "ไฟล์เอกสาร"}
                  {formatFileSize(document.sizeBytes) ? ` · ${formatFileSize(document.sizeBytes)}` : ""}
                  {document.uploadedAtUtc ? ` · ${formatDateTimeTh(document.uploadedAtUtc)}` : ""}
                </span>
              </div>
              {canEdit && (
                <button
                  type="button"
                  className="wx-wdp-repeat-remove"
                  onClick={() => handleDelete(document.documentId)}
                  disabled={busyId === document.documentId}
                  aria-label={`ลบเอกสาร ${document.documentType || document.fileName || ""}`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="wx-wdp-extra-add">
          <label className="wx-wdp-field-control wx-wdp-extra-type">
            <span className="wx-wdp-field-label">ชื่อเอกสาร</span>
            <input
              type="text"
              maxLength={120}
              list="wx-wdp-doctypes"
              placeholder="เช่น ใบรับรองแพทย์"
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="wx-button wx-button--ghost"
            onClick={() => inputRef.current?.click()}
            disabled={state.status === "uploading"}
          >
            <Upload size={15} aria-hidden="true" />
            {state.status === "uploading" ? "กำลังอัปโหลด..." : "แนบเอกสารเพิ่มเติม"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="wx-wdp-doc-input"
            aria-label="เลือกไฟล์เอกสารเพิ่มเติม"
            onChange={handleFile}
          />
        </div>
      )}

      {state.status === "error" && (
        <p className="wx-wdp-doc-error" role="alert">
          {state.message}
        </p>
      )}
    </div>
  );
}

/**
 * P157-R9: the worker's own photo, in the avatar slot the placeholder glyph used
 * to occupy. Uploading replaces it in place - there is one photo per worker, so
 * a second upload overwrites the first rather than accumulating, which is what
 * the single set of Photo* columns on the application row means.
 *
 * `capture="environment"` on the input so a phone or tablet opens its camera
 * straight away; on a desktop it is an ordinary file picker.
 */
function WorkerPhoto({ applicationId, photo, workerName, canEdit, onUploaded }) {
  const inputRef = useRef(null);
  const [state, setState] = useState({ status: "idle", message: null });

  async function handleFile(event) {
    const file = event.target.files?.[0];
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (!file) {
      return;
    }
    setState({ status: "uploading", message: null });
    try {
      const updated = await uploadWorkerPhoto(applicationId, file);
      onUploaded?.(updated);
      setState({ status: "idle", message: null });
    } catch (error) {
      setState({
        status: "error",
        message: error?.body?.message ?? "อัปโหลดรูปถ่ายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      });
    }
  }

  const hasPhoto = photo?.hasDocument === true;

  return (
    <div className="wx-wdp-photo">
      <div className={`wx-wdp-avatar${hasPhoto ? " wx-wdp-avatar--photo" : ""}`}>
        {hasPhoto ? (
          <StoredFileImage
            path={`/backoffice/worker-applications/${encodeURIComponent(applicationId)}/detailed-profile/photo/file`}
            label={`รูปถ่ายของ ${workerName}`}
            present
          />
        ) : (
          <UserRound size={44} strokeWidth={1.75} aria-hidden="true" />
        )}
      </div>

      {canEdit && (
        <>
          <button
            type="button"
            className="wx-wdp-photo-action"
            onClick={() => inputRef.current?.click()}
            disabled={state.status === "uploading"}
          >
            <Camera size={13} aria-hidden="true" />
            {state.status === "uploading" ? "กำลังอัปโหลด..." : hasPhoto ? "เปลี่ยนรูปถ่าย" : "แนบรูปถ่าย"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="wx-wdp-doc-input"
            aria-label="เลือกรูปถ่ายของแรงงาน"
            onChange={handleFile}
          />
        </>
      )}

      {state.status === "error" && (
        <p className="wx-wdp-photo-error" role="alert">
          {state.message}
        </p>
      )}
    </div>
  );
}

function FieldGroup({ Icon, children }) {
  return (
    <h4 className="wx-wdp-group">
      {Icon && <Icon size={16} aria-hidden="true" />}
      <span>{children}</span>
    </h4>
  );
}

/**
 * P157-R6: `missing` now renders as a red asterisk on the label rather than an
 * amber-tinted box plus a "ยังขาด" caption under every empty control. Same
 * server-supplied fact, a fraction of the noise, and it reads the way a required
 * field reads everywhere else.
 *
 * The visible asterisk is CSS generated content on the label (see the stylesheet
 * for why it cannot be a real element), and the meaning is carried by real text
 * in a visually-hidden sibling, because "*" alone announces as "star" and tells a
 * screen reader nothing.
 */
function Field({ label, hint, missing, name, span, children }) {
  return (
    <div
      // P157-R5: `data-field` is how the focus helper finds the control a label
      // names, without giving every input an id and an htmlFor (the labels are
      // implicit and must stay that way - see this component's own note above).
      data-field={name}
      className={`wx-wdp-field${span ? ` wx-wdp-field--${span}` : ""}${missing ? " wx-wdp-field--missing" : ""}`}
    >
      <label className="wx-wdp-field-control">
        <span className="wx-wdp-field-label">{label}</span>
        {children}
      </label>
      {missing && <span className="wx-wdp-req wx-visually-hidden">ข้อมูลที่ยังขาดสำหรับการจับคู่งาน</span>}
      {hint && <span className="wx-wdp-field-hint">{hint}</span>}
    </div>
  );
}

/**
 * P146: passport/identity document capture. The file input is always present
 * (the required fallback), and it carries `capture="environment"` so a phone
 * opens its camera directly. The live-camera panel is only offered when the
 * browser genuinely exposes navigator.mediaDevices.getUserMedia - it is never
 * rendered as a dead button on a browser that cannot do it.
 */
function DocumentUploader({ document: documentMeta, disabled, onUpload, uploadState, missing, applicationId, workerName }) {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const cameraSupported =
    typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";

  const stopCamera = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      for (const track of stream.getTracks?.() ?? []) {
        track.stop?.();
      }
      streamRef.current = null;
    }
    setCameraOpen(false);
  }, []);

  // Releasing the camera on unmount matters here specifically: this editor
  // lives inside a drawer that is closed rather than navigated away from, so
  // without this the capture light would stay on after the drawer disappears.
  useEffect(() => stopCamera, [stopCamera]);

  async function handleOpenCamera() {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setCameraOpen(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play?.();
      }
    } catch {
      setCameraError("เปิดกล้องไม่ได้ กรุณาอนุญาตการใช้กล้อง หรือเลือกไฟล์จากเครื่องแทน");
      stopCamera();
    }
  }

  async function handleCapture() {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const canvas = window.document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => {
      if (typeof canvas.toBlob === "function") {
        canvas.toBlob(resolve, "image/jpeg", 0.92);
      } else {
        resolve(null);
      }
    });
    stopCamera();
    if (!blob) {
      setCameraError("บันทึกภาพจากกล้องไม่ได้ กรุณาเลือกไฟล์จากเครื่องแทน");
      return;
    }
    await onUpload(new File([blob], "passport-capture.jpg", { type: "image/jpeg" }));
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (file) {
      await onUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  const sizeLabel = formatFileSize(documentMeta?.sizeBytes);

  return (
    <div className="wx-wdp-doc" data-field="passportDocument">
      {/* P157-R5: a real group heading, the same one every other block in the
          dialog uses, instead of a dashed rule plus a field-label pretending to
          be one. */}
      <h4 className="wx-wdp-group">ไฟล์เอกสารหนังสือเดินทาง</h4>
      <div className="wx-wdp-doc-status">
        {documentMeta?.hasDocument ? (
          <p className="wx-wdp-doc-have">
            อัปโหลดแล้ว · {documentMeta.fileName || "ไฟล์เอกสาร"}
            {sizeLabel ? ` · ${sizeLabel}` : ""}
            {documentMeta.uploadedAtUtc ? ` · ${formatDateTimeTh(documentMeta.uploadedAtUtc)}` : ""}
          </p>
        ) : (
          <p className="wx-wdp-doc-none">
            ยังไม่มีไฟล์เอกสาร
            {missing && <span className="wx-wdp-field-flag">ยังขาด</span>}
          </p>
        )}
      </div>

      {/* P157-R8: the real document, not just its file name. Until now this tab
          reported "passport_front.jpg · 240 กิโลไบต์" - which reads as a path and
          cannot be used to verify anybody - while a working authenticated read
          endpoint (GET .../passport-document/file, P141R16) and a viewer built on
          it had existed since P157. Clicking the thumbnail opens it full size.
          A 404 renders as an honest "no document" state, never a placeholder. */}
      <PassportDocumentViewer
        applicationId={applicationId}
        workerName={workerName}
        hasDocument={Boolean(documentMeta?.hasDocument)}
      />

      {!disabled && (
        <div className="wx-wdp-doc-actions">
          <button
            type="button"
            className="wx-button wx-button--ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadState === "uploading"}
          >
            <Upload size={15} aria-hidden="true" />
            เลือกไฟล์จากเครื่อง
          </button>
          {cameraSupported && !cameraOpen && (
            <button
              type="button"
              className="wx-button wx-button--ghost"
              onClick={handleOpenCamera}
              disabled={uploadState === "uploading"}
            >
              <Camera size={15} aria-hidden="true" />
              ถ่ายภาพด้วยกล้อง
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="wx-wdp-doc-input"
            aria-label="เลือกไฟล์เอกสารหนังสือเดินทาง"
            onChange={handleFileChange}
          />
        </div>
      )}

      {cameraOpen && (
        <div className="wx-wdp-doc-camera">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={videoRef} className="wx-wdp-doc-video" playsInline muted />
          <div className="wx-wdp-doc-actions">
            <button type="button" className="wx-button wx-button--primary" onClick={handleCapture}>
              บันทึกภาพนี้
            </button>
            <button type="button" className="wx-button wx-button--ghost" onClick={stopCamera}>
              ปิดกล้อง
            </button>
          </div>
        </div>
      )}

      {uploadState === "uploading" && <p className="wx-wdp-doc-note">กำลังอัปโหลดเอกสาร...</p>}
      {uploadState === "error" && (
        <p className="wx-wdp-doc-error" role="alert">
          อัปโหลดเอกสารไม่สำเร็จ กรุณาตรวจสอบว่าเป็นไฟล์รูปภาพและลองใหม่อีกครั้ง
        </p>
      )}
      {cameraError && (
        <p className="wx-wdp-doc-error" role="alert">
          {cameraError}
        </p>
      )}
    </div>
  );
}

export function WorkerDetailedProfileEditor({
  applicationId,
  titleId,
  onSaved,
  onRequestClose,
  onDirtyChange,
  confirmingClose = false,
  onCancelClose,
  onDiscardClose,
}) {
  const { status, data: profile, error, applyProfile } = useWorkerDetailedProfile(applicationId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [rows, setRows] = useState(() => toRowsState(null));
  const [activeSection, setActiveSection] = useState("Identity");
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState({ status: "idle", message: null });
  const [uploadState, setUploadState] = useState("idle");

  // Re-seed the form only when a genuinely different profile payload
  // arrives. A save response reseeds it too (that is what clears `dirty`
  // and shows the server's own normalized values back), which is why the
  // effect keys on the profile object rather than on applicationId alone.
  useEffect(() => {
    if (profile) {
      setForm(toFormState(profile));
      setRows(toRowsState(profile));
      setDirty(false);
    }
  }, [profile]);

  const canEdit = profile?.canEdit === true;
  const options = profile?.options ?? {};

  // The host dialog owns the close policy (Escape, backdrop, the close button),
  // so it needs to know there is work worth protecting before it acts on any of
  // them. Reported rather than asked for, so the editor stays the single owner
  // of what "dirty" means.
  useEffect(() => {
    onDirtyChange?.(dirty && canEdit);
  }, [dirty, canEdit, onDirtyChange]);

  const setField = useCallback((key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setDirty(true);
    setSaveState({ status: "idle", message: null });
  }, []);

  const inputProps = useCallback(
    (key) => ({
      value: form[key] ?? "",
      onChange: (event) => setField(key, event.target.value),
      disabled: !canEdit,
    }),
    [form, canEdit, setField],
  );

  const addRow = useCallback((sectionKey) => {
    setRows((previous) => ({
      ...previous,
      [sectionKey]: [...previous[sectionKey], { ...EMPTY_ROWS[sectionKey] }],
    }));
    setDirty(true);
    setSaveState({ status: "idle", message: null });
  }, []);

  const removeRow = useCallback((sectionKey, index) => {
    setRows((previous) => ({
      ...previous,
      [sectionKey]: previous[sectionKey].filter((_, i) => i !== index),
    }));
    setDirty(true);
    setSaveState({ status: "idle", message: null });
  }, []);

  const setRowField = useCallback((sectionKey, index, field, value) => {
    setRows((previous) => ({
      ...previous,
      [sectionKey]: previous[sectionKey].map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    }));
    setDirty(true);
    setSaveState({ status: "idle", message: null });
  }, []);

  const rowProps = useCallback(
    (sectionKey, index, field) => ({
      value: rows[sectionKey]?.[index]?.[field] ?? "",
      onChange: (event) => setRowField(sectionKey, index, field, event.target.value),
      disabled: !canEdit,
    }),
    [rows, canEdit, setRowField],
  );

  /**
   * P147: the save payload. Sends every flat field (an omitted one clears the
   * stored value - the backend's documented rule, which is why this is a
   * whole-form submit rather than a per-tab one) and all five sections
   * explicitly, so a section emptied on screen is genuinely emptied on save.
   */
  const buildPayload = useCallback(
    () => ({
      ...form,
      smokingFlag: fromFlagValue(form.smokingFlag),
      drinkingFlag: fromFlagValue(form.drinkingFlag),
      covidVaccinationFlag: fromFlagValue(form.covidVaccinationFlag),
      languages: rows.languages.map((row) => ({
        languageName: toOptionalText(row.languageName),
        listeningLevel: toOptionalText(row.listeningLevel),
        speakingLevel: toOptionalText(row.speakingLevel),
        readingLevel: toOptionalText(row.readingLevel),
        writingLevel: toOptionalText(row.writingLevel),
      })),
      skillItems: rows.skillItems.map((row) => ({
        skillName: toOptionalText(row.skillName),
        experienceYears: toOptionalInt(row.experienceYears),
      })),
      educations: rows.educations.map((row) => ({
        institutionName: toOptionalText(row.institutionName),
        educationLevelText: toOptionalText(row.educationLevelText),
        major: toOptionalText(row.major),
        startYear: toOptionalInt(row.startYear),
        endYear: toOptionalInt(row.endYear),
        gpaText: toOptionalText(row.gpaText),
      })),
      experiences: rows.experiences.map((row) => ({
        companyName: toOptionalText(row.companyName),
        jobTitle: toOptionalText(row.jobTitle),
        countryName: toOptionalText(row.countryName),
        startYear: toOptionalInt(row.startYear),
        endYear: toOptionalInt(row.endYear),
        salaryText: toOptionalText(row.salaryText),
        remark: toOptionalText(row.remark),
      })),
      visas: rows.visas.map((row) => ({
        visaType: toOptionalText(row.visaType),
        issuingCountry: toOptionalText(row.issuingCountry),
        visaNumber: toOptionalText(row.visaNumber),
        issueDate: toOptionalText(row.issueDate),
        expiryDate: toOptionalText(row.expiryDate),
      })),
    }),
    [form, rows],
  );

  async function handleSave() {
    if (!canEdit) {
      return;
    }
    setSaveState({ status: "saving", message: null });
    try {
      const saved = await saveWorkerDetailedProfile(applicationId, buildPayload());
      applyProfile(saved);
      setSaveState({ status: "saved", message: "บันทึกข้อมูลเรียบร้อยแล้ว" });
      // Lets the surrounding list/drawer/dashboard re-read the same worker,
      // so a name corrected here cannot stay stale in the row behind it.
      onSaved?.(saved);
    } catch (saveError) {
      setSaveState({ status: "error", message: describeSaveError(saveError) });
    }
  }

  async function handleUpload(file) {
    setUploadState("uploading");
    try {
      const updated = await uploadWorkerPassportDocument(applicationId, file);
      applyProfile(updated);
      setUploadState("idle");
      onSaved?.(updated);
    } catch {
      setUploadState("error");
    }
  }

  /**
   * Show one section. Was a scroll-jump down a single very long form with a
   * 176px vertical rail beside it; the rail is now tabs across the top and only
   * the chosen section is mounted (2026-08-03, owner: the side rail "ใช้เนื้อที่
   * แปลกๆ").
   *
   * Unmounting the other sections is safe because every field is controlled off
   * the single `form` state object and `buildPayload` reads that object, not the
   * DOM - so a value typed on one tab is still submitted while another tab is
   * showing. The checklist's "go and fix this" links keep working unchanged:
   * switching tab IS the jump now. An unknown key (Notes has no checklist entry)
   * still simply does nothing.
   */
  const jumpToSection = useCallback((sectionKey) => {
    if (!SECTIONS.some((section) => section.key === sectionKey)) {
      return;
    }
    setActiveSection(sectionKey);
  }, []);

  /**
   * P157-R4: how many still-empty match-critical fields each tab holds, and
   * which individual fields they are.
   *
   * This is the change that makes the dialog navigable. The backend already
   * tells us the section of every missing field; nothing here used it, so an
   * operator had to open all six tabs to discover which two actually needed
   * work. The counts ride on the tabs, and `missingFieldKeys` marks the exact
   * empty control once you get there.
   */
  const missingBySection = useMemo(() => {
    const counts = {};
    for (const field of profile?.completeness?.missingFields ?? []) {
      counts[field.section] = (counts[field.section] ?? 0) + 1;
    }
    return counts;
  }, [profile]);

  const missingFieldKeys = useMemo(
    () => new Set((profile?.completeness?.missingFields ?? []).map((field) => field.field)),
    [profile],
  );

  const tabRefs = useRef({});
  const panelRef = useRef(null);

  /**
   * P157-R5: put the cursor in the box a checklist chip names. Scoped to the
   * open panel, so it can only ever reach a control that is actually mounted.
   * Falls back to scrolling the block into view for the four checklist entries
   * that are not a single input (the emergency contact pair, the passport
   * document uploader, and the three repeating sections).
   */
  const focusField = useCallback((fieldName) => {
    const block = panelRef.current?.querySelector(`[data-field="${fieldName}"]`);
    if (!block) {
      return;
    }
    const control = block.matches("input, select, textarea")
      ? block
      : block.querySelector("input:not([type=file]), select, textarea, button");
    if (control && !control.disabled) {
      control.focus();
    }
    // Optional call: scrollIntoView is not implemented in jsdom, and focusing is
    // the part that matters - scrolling is the nicety on top. Unguarded, this
    // threw an uncaught TypeError through React's event dispatch, which vitest
    // reports as an unhandled error (exit 1) even while every assertion passes.
    block.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, []);

  /**
   * Roving arrow-key navigation, which the tabs never had - they carried the
   * tab/tablist roles and aria-selected without any of the keyboard behaviour
   * those roles promise, so a keyboard user got a role contract the widget did
   * not honour. Left/Right wrap, Home/End jump to the ends.
   */
  const handleTabKeyDown = useCallback(
    (event) => {
      const index = SECTIONS.findIndex((section) => section.key === activeSection);
      let nextIndex = null;
      if (event.key === "ArrowRight") {
        nextIndex = (index + 1) % SECTIONS.length;
      } else if (event.key === "ArrowLeft") {
        nextIndex = (index - 1 + SECTIONS.length) % SECTIONS.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = SECTIONS.length - 1;
      }
      if (nextIndex === null) {
        return;
      }
      event.preventDefault();
      const nextKey = SECTIONS[nextIndex].key;
      setActiveSection(nextKey);
      tabRefs.current[nextKey]?.focus();
    },
    [activeSection],
  );

  function handleCloseRequest() {
    onRequestClose?.();
  }

  const applicationCode = profile?.quickRegister?.applicationId ?? applicationId;
  const lineLabel = [
    profile?.quickRegister?.agent?.displayName,
    profile?.quickRegister?.line?.plcName,
    profile?.quickRegister?.line?.superAgentName,
  ]
    .filter(Boolean)
    .join(" · ");

  // Operators paste this id into other systems, so it gets a real copy control
  // rather than a select-and-drag on a line that sits inside a scrolling dialog.
  const [copied, setCopied] = useState(false);
  async function handleCopyCode() {
    const ok = await copyTextToClipboard(applicationCode);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  const headline = useMemo(() => {
    const detail = profile?.detail ?? {};
    return detail.fullNameNative || detail.fullNameEnglish || profile?.quickRegister?.fullNameNative || "แรงงาน";
  }, [profile]);

  if ((status === "loading" || status === "idle") && !profile) {
    return (
      <DetailSkeleton label="กำลังโหลดข้อมูลโปรไฟล์แรงงาน..." />
    );
  }

  if (status === "error" && !profile) {
    return (
      <div className="wx-wdp-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถเปิดโปรไฟล์แรงงานได้
        </h2>
        <p className="wx-state-body">{describeLoadError(error)}</p>
      </div>
    );
  }

  return (
    <div className="wx-wdp">
      {/* Pinned identity band. Was part of the single scrolling column, so the
          name, the status pills and the completeness meter all scrolled away
          after the first field - the operator lost both "who am I editing" and
          "how much is left" for the rest of the session. */}
      <header className="wx-wdp-head">
        <p className="wx-wdp-kicker">
          <UserCog size={17} aria-hidden="true" />
          โปรไฟล์แรงงานแบบละเอียด
        </p>
        <div className="wx-wdp-head-main">
          <WorkerPhoto
            applicationId={applicationId}
            photo={profile?.photo}
            workerName={headline}
            canEdit={canEdit}
            onUploaded={applyProfile}
          />
          <div className="wx-wdp-head-id">
            <h2 id={titleId} className="wx-wdp-title">
              {headline}
            </h2>
            <p className="wx-wdp-sub">
              รหัสใบสมัคร
              <span className="wx-wdp-mono">{applicationCode}</span>
              <button
                type="button"
                className="wx-wdp-copy"
                onClick={handleCopyCode}
                aria-label={copied ? "คัดลอกรหัสใบสมัครแล้ว" : "คัดลอกรหัสใบสมัคร"}
              >
                {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
              </button>
              {/* The one value the deleted Quick Register panel held that lives
                  nowhere else in this dialog. It rides on a line that already
                  exists, so it costs no height. */}
              {lineLabel && (
                <>
                  <span className="wx-wdp-sub-sep" aria-hidden="true">·</span>
                  สายงาน <strong className="wx-wdp-sub-strong">{lineLabel}</strong>
                </>
              )}
            </p>
            <StatusPills statuses={profile?.statuses} completeness={profile?.completeness} />
          </div>
          <div className="wx-wdp-head-aside">
            <CompletenessMeter completeness={profile?.completeness} />
            <button
              type="button"
              className="wx-wdp-close"
              onClick={handleCloseRequest}
              aria-label="ปิดหน้าต่างแก้ไขโปรไฟล์"
            >
              <X size={17} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs across the top, not a rail down the side. This editor lives in a
          drawer that is already narrow, and the rail took 176px plus a gap out
          of the form's width for six links - so the fields beside it wrapped
          far sooner than they needed to. It also sat directly under the
          drawer's OWN row of tabs, which read as two competing navigations for
          one panel. */}
      {/* P157-R7: suggestion lists, deliberately NOT <select>. The backend serves
          no enum for these three columns and accepts any string, so a closed
          dropdown would invent a vocabulary the server does not have and would
          block the values it does accept (a Burmese honorific, a currency that
          is not THB). A datalist gives the dropdown affordance and still takes
          free text. Every field that DOES have a real server list - เพศ, จังหวัด,
          กรุ๊ปเลือด, ความถนัดหลัก, รอบการจ่าย, ระดับภาษา - is a real select. */}
      <datalist id="wx-wdp-titles">
        <option value="นาย" />
        <option value="นาง" />
        <option value="นางสาว" />
      </datalist>
      <datalist id="wx-wdp-currencies">
        <option value="THB" />
        <option value="USD" />
      </datalist>
      <datalist id="wx-wdp-doctypes">
        <option value="ใบรับรองแพทย์" />
        <option value="วีซ่า" />
        <option value="ใบอนุญาตทำงาน" />
        <option value="สัญญาจ้าง" />
        <option value="ทะเบียนบ้าน" />
        <option value="วุฒิการศึกษา" />
      </datalist>
      <datalist id="wx-wdp-relations">
        <option value="บิดา" />
        <option value="มารดา" />
        <option value="คู่สมรส" />
        <option value="บุตร" />
        <option value="พี่น้อง" />
        <option value="ญาติ" />
        <option value="เพื่อน" />
      </datalist>

      <div className="wx-wdp-body">
      <div className="wx-wdp-tabs">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
        <nav className="wx-wdp-nav" aria-label="หัวข้อข้อมูลแรงงาน" role="tablist" onKeyDown={handleTabKeyDown}>
          {SECTIONS.map(({ key, label, Icon }) => {
            const count = missingBySection[key] ?? 0;
            const active = activeSection === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                id={`wx-wdp-tab-${key}`}
                ref={(node) => {
                  tabRefs.current[key] = node;
                }}
                className={`wx-wdp-nav-item ${active ? "wx-wdp-nav-item--active" : ""}`}
                aria-selected={active}
                aria-controls={`wx-wdp-panel-${key}`}
                aria-describedby={count > 0 ? `wx-wdp-tabdesc-${key}` : undefined}
                tabIndex={active ? 0 : -1}
                onClick={() => jumpToSection(key)}
              >
                <Icon size={16} aria-hidden="true" />
                <span>{label}</span>
                {count > 0 && (
                  <span className="wx-wdp-tab-count" aria-hidden="true">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        {/* The count rides in aria-describedby, never inside the button: folded
            into the label it would rename the tab to "ข้อมูลตัวตน 4" and make it
            unfindable by the name a person actually reads. */}
        <div className="wx-visually-hidden">
          {SECTIONS.map(({ key }) =>
            (missingBySection[key] ?? 0) > 0 ? (
              <span key={key} id={`wx-wdp-tabdesc-${key}`}>
                ยังขาด {missingBySection[key]} ช่อง
              </span>
            ) : null,
          )}
        </div>
      </div>

      <div className="wx-wdp-scroll" ref={panelRef}>
        <div className="wx-wdp-main">
          {!canEdit && (
            <p className="wx-wdp-readonly" role="status">
              <Lock size={15} aria-hidden="true" />
              คุณดูข้อมูลนี้ได้อย่างเดียว การแก้ไขทำได้เฉพาะผู้ดูแลระบบและผู้ที่รับผิดชอบสายงานของแรงงานรายนี้
            </p>
          )}
          <CompleteNotice completeness={profile?.completeness} />

          {activeSection === "Identity" && (
          <section
            className="wx-wdp-section"
            id="wx-wdp-panel-Identity"
            role="tabpanel"
            aria-labelledby="wx-wdp-tab-Identity"
          >
            {/* No <h3> in any panel: the selected tab directly above already
                says "ข้อมูลตัวตน", and aria-labelledby names the panel from it.
                A heading that restates the tab you just pressed is noise. */}
            {/* One grid per panel, with each field declaring how many columns it
                deserves. Fields used to escape the grid whenever they needed more
                room, which left a lone select stretched across 950px on one row
                and four tight boxes on the next. */}
            <div className="wx-wdp-grid">
              <FieldGroup Icon={User}>ชื่อตามเอกสาร</FieldGroup>
              <Field label="คำนำหน้า">
                <input type="text" maxLength={40} list="wx-wdp-titles" placeholder="เลือกหรือพิมพ์คำนำหน้า" {...inputProps("titlePrefix")} />
              </Field>
              <Field label="ชื่อ" name="firstName" missing={missingFieldKeys.has("firstName")}>
                <input type="text" maxLength={120} placeholder="กรอกชื่อ" {...inputProps("firstName")} />
              </Field>
              <Field label="นามสกุล" name="lastName" missing={missingFieldKeys.has("lastName")}>
                <input type="text" maxLength={120} placeholder="กรอกนามสกุล" {...inputProps("lastName")} />
              </Field>
              <Field label="เลขบัตรประจำตัว">
                <input type="text" maxLength={40} placeholder="กรอกเลขบัตรประจำตัว" {...inputProps("citizenNo")} />
              </Field>
              <Field label="ชื่อ (EN)">
                <input type="text" maxLength={120} placeholder="Enter given name" {...inputProps("firstNameEn")} />
              </Field>
              <Field label="นามสกุล (EN)">
                <input type="text" maxLength={120} placeholder="Enter family name" {...inputProps("lastNameEn")} />
              </Field>
              <Field
                label="ชื่อ-นามสกุล"
                name="fullNameNative"
                span="wide"
                hint="ชื่อเต็มตามที่บันทึกไว้ตอนสมัคร"
                missing={missingFieldKeys.has("fullNameNative")}
              >
                <input type="text" maxLength={200} placeholder="กรอกชื่อ-นามสกุล" {...inputProps("fullNameNative")} />
              </Field>
              <Field label="ชื่อภาษาอังกฤษ" span="wide">
                <input type="text" maxLength={200} placeholder="Enter full name in English" {...inputProps("fullNameEnglish")} />
              </Field>
              <Field label="ชื่อท้องถิ่น" span="wide" hint="ชื่อในภาษาของประเทศต้นทาง ถ้ามี">
                <input type="text" maxLength={200} placeholder="กรอกชื่อท้องถิ่น" {...inputProps("localName")} />
              </Field>

              <FieldGroup Icon={IdCard}>ข้อมูลส่วนบุคคล</FieldGroup>
              <Field label="เพศ" name="genderCode" missing={missingFieldKeys.has("genderCode")}>
                <select {...inputProps("genderCode")}>
                  <option value="">เลือกเพศ</option>
                  {(options.genders ?? []).map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="วันเกิด" name="dateOfBirth" missing={missingFieldKeys.has("dateOfBirth")}>
                <input type="date" {...inputProps("dateOfBirth")} />
              </Field>
              <Field label="สัญชาติ" name="nationality" missing={missingFieldKeys.has("nationality")}>
                <input type="text" maxLength={40} placeholder="กรอกสัญชาติ" {...inputProps("nationality")} />
              </Field>
            </div>
          </section>
          )}

          {activeSection === "Contact" && (
          <section
            className="wx-wdp-section"
            id="wx-wdp-panel-Contact"
            role="tabpanel"
            aria-labelledby="wx-wdp-tab-Contact"
          >
            <div className="wx-wdp-grid">
              <FieldGroup Icon={Phone}>ช่องทางติดต่อ</FieldGroup>
              <Field label="เบอร์โทรติดต่อ" name="phoneNumber" missing={missingFieldKeys.has("phoneNumber")}>
                <input type="tel" maxLength={32} {...inputProps("phoneNumber")} />
              </Field>
              <Field label="WhatsApp">
                <input type="tel" maxLength={32} {...inputProps("whatsAppNumber")} />
              </Field>
              <Field label="อีเมล">
                <input type="email" maxLength={200} {...inputProps("email")} />
              </Field>
              <Field label="ภาษาที่ถนัด" hint="เช่น bn, my, km">
                <input type="text" maxLength={10} {...inputProps("preferredLanguageCode")} />
              </Field>

              <FieldGroup Icon={Home}>ที่อยู่</FieldGroup>
              <Field
                label="ที่อยู่ประเทศต้นทาง"
                name="homeCountryAddress"
                span="wide"
                missing={missingFieldKeys.has("homeCountryAddress")}
              >
                <textarea rows={3} maxLength={500} {...inputProps("homeCountryAddress")} />
              </Field>
              {/* A separate, real fact from the address above - where the worker
                  lives in Thailand, not where they came from. Side by side so the
                  difference between the two is visible at a glance. */}
              <Field
                label="ที่อยู่ปัจจุบัน"
                name="currentAddress"
                span="wide"
                missing={missingFieldKeys.has("currentAddress")}
              >
                <textarea rows={3} maxLength={500} {...inputProps("currentAddress")} />
              </Field>
              <Field
                label="จังหวัดที่อยู่ปัจจุบัน"
                name="currentProvince"
                missing={missingFieldKeys.has("currentProvince")}
              >
                <select {...inputProps("currentProvince")}>
                  <option value="">ยังไม่ระบุ</option>
                  {(options.provinces ?? []).map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              {/* One completeness item covers both, so both carry the marker -
                  an emergency contact with only half of it on file is not usable
                  and the backend counts it as still missing. */}
              <FieldGroup Icon={Contact}>ผู้ติดต่อฉุกเฉิน</FieldGroup>
              <Field label="ชื่อผู้ติดต่อ" name="emergencyContact" missing={missingFieldKeys.has("emergencyContact")}>
                <input type="text" maxLength={200} {...inputProps("emergencyContactName")} />
              </Field>
              <Field label="เบอร์ผู้ติดต่อ" missing={missingFieldKeys.has("emergencyContact")}>
                <input type="tel" maxLength={32} {...inputProps("emergencyContactPhone")} />
              </Field>
              <Field label="ความสัมพันธ์" hint="เช่น บิดา มารดา คู่สมรส">
                <input type="text" maxLength={120} list="wx-wdp-relations" placeholder="เลือกหรือพิมพ์ความสัมพันธ์" {...inputProps("emergencyContactRelation")} />
              </Field>
            </div>
          </section>
          )}

          {activeSection === "Documents" && (
          <section
            className="wx-wdp-section"
            id="wx-wdp-panel-Documents"
            role="tabpanel"
            aria-labelledby="wx-wdp-tab-Documents"
          >
            <div className="wx-wdp-grid">
              <FieldGroup Icon={FileText}>หนังสือเดินทาง</FieldGroup>
              <Field
                label="หมายเลขหนังสือเดินทาง"
                name="passportNumber"
                span="wide"
                hint={
                  profile?.detail?.passportNumberMasked
                    ? `บันทึกไว้แล้ว: ${profile.detail.passportNumberMasked} · กรอกใหม่เฉพาะเมื่อต้องการเปลี่ยน`
                    : "ยังไม่มีหมายเลขที่บันทึกไว้"
                }
                missing={missingFieldKeys.has("passportNumber")}
              >
                <input type="text" maxLength={40} {...inputProps("passportNumber")} />
              </Field>
              <Field
                label="วันหมดอายุหนังสือเดินทาง"
                name="passportExpiryDate"
                missing={missingFieldKeys.has("passportExpiryDate")}
              >
                <input type="date" {...inputProps("passportExpiryDate")} />
              </Field>
              <Field
                label="ประเทศที่ออกเอกสาร"
                name="passportIssuingCountry"
                missing={missingFieldKeys.has("passportIssuingCountry")}
              >
                <input type="text" maxLength={40} {...inputProps("passportIssuingCountry")} />
              </Field>
            </div>
            <DocumentUploader
              document={profile?.document}
              disabled={!canEdit}
              onUpload={handleUpload}
              uploadState={uploadState}
              missing={missingFieldKeys.has("passportDocument")}
              applicationId={applicationId}
              workerName={headline}
            />
            <ExtraDocuments
              applicationId={applicationId}
              documents={profile?.documents}
              canEdit={canEdit}
              onChanged={applyProfile}
            />
          </section>
          )}

          {activeSection === "Health" && (
          <section
            className="wx-wdp-section"
            id="wx-wdp-panel-Health"
            role="tabpanel"
            aria-labelledby="wx-wdp-tab-Health"
          >
            <div className="wx-wdp-grid">
              <Field label="กรุ๊ปเลือด">
                <select {...inputProps("bloodType")}>
                  <option value="">ยังไม่ระบุ</option>
                  {(options.bloodTypes ?? []).map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              {/* "ยังไม่ได้ถาม" is a real third answer, kept distinct from
                  "ไม่" - a health question nobody asked must not read as a no. */}
              <Field label="สูบบุหรี่">
                <select {...inputProps("smokingFlag")}>
                  {YES_NO_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="ดื่มแอลกอฮอล์">
                <select {...inputProps("drinkingFlag")}>
                  {YES_NO_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="วัคซีน COVID-19">
                <select {...inputProps("covidVaccinationFlag")}>
                  {YES_NO_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="โรคประจำตัว" span="wide" hint="ระบุโรค หรือปล่อยว่างหากไม่มี">
                <textarea rows={2} maxLength={500} {...inputProps("medicalCondition")} />
              </Field>
            </div>
          </section>
          )}

          {activeSection === "Skills" && (
          <section
            className="wx-wdp-section"
            id="wx-wdp-panel-Skills"
            role="tabpanel"
            aria-labelledby="wx-wdp-tab-Skills"
          >
            <div className="wx-wdp-grid">
              <FieldGroup Icon={GraduationCap}>ความถนัดและประสบการณ์</FieldGroup>
              <Field label="ความถนัดหลัก" name="primarySkillCode" missing={missingFieldKeys.has("primarySkillCode")}>
                <select {...inputProps("primarySkillCode")}>
                  <option value="">ยังไม่ระบุ</option>
                  {/* P147: a worker whose stored code predates the fixed list
                      (WAREHOUSE, DRIVER, ...) must still see their own value.
                      Without this the control renders blank, which looks like
                      "not set" and silently rewrites a real record on save. */}
                  {form.primarySkillCode &&
                    !(options.primarySkills ?? []).some((option) => option.code === form.primarySkillCode) && (
                      <option value={form.primarySkillCode}>
                        {profile?.detail?.primarySkillName || form.primarySkillCode} (ค่าเดิมในระบบ)
                      </option>
                    )}
                  {(options.primarySkills ?? []).map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              {form.primarySkillCode === OTHER_SKILL_CODE && (
                <Field label="ระบุความถนัดหลัก" hint="เช่น งานครัว, แม่บ้าน, คนขับรถ">
                  <input type="text" maxLength={100} {...inputProps("primarySkillOtherText")} />
                </Field>
              )}
              <Field label="ประสบการณ์ (ปี)" name="yearsOfExperience" missing={missingFieldKeys.has("yearsOfExperience")}>
                <input type="number" min={0} max={60} {...inputProps("yearsOfExperience")} />
              </Field>
              {/* Ordered so the two-column fields pair off. Left in source order
                  they each landed alone on a row, and three rows running with the
                  same right half empty reads as a hole in the form. */}
              <Field
                label="ภาษาที่สื่อสารได้"
                name="spokenLanguages"
                span="wide"
                missing={missingFieldKeys.has("spokenLanguages")}
              >
                <input type="text" maxLength={300} {...inputProps("spokenLanguages")} />
              </Field>
              <Field label="ระดับการศึกษา (สรุป)" span="wide" hint="เช่น มัธยมศึกษาตอนปลาย ปวช. ปริญญาตรี">
                <input type="text" maxLength={200} {...inputProps("educationLevelSummary")} />
              </Field>
              <Field label="ทักษะรอง" span="wide" hint="คั่นแต่ละรายการด้วยเครื่องหมายจุลภาค">
                <input type="text" maxLength={500} {...inputProps("secondarySkills")} />
              </Field>
              <Field label="วุฒิ/ใบรับรอง" span="wide">
                <input type="text" maxLength={500} {...inputProps("certifications")} />
              </Field>

              <FieldGroup Icon={MapPin}>งานที่ต้องการ</FieldGroup>
              <Field
                label="ประเภทงานที่ต้องการ"
                name="desiredJobTypes"
                span="wide"
                missing={missingFieldKeys.has("desiredJobTypes")}
              >
                <input type="text" maxLength={300} {...inputProps("desiredJobTypes")} />
              </Field>
              <Field label="วันที่พร้อมเริ่มงาน" name="availableFromDate" missing={missingFieldKeys.has("availableFromDate")}>
                <input type="date" {...inputProps("availableFromDate")} />
              </Field>
              {/* A textarea with no partner on its row takes the whole row: half
                  a row of empty grid beside a two-line box is the void; a full
                  row is a deliberate measure. */}
              <Field
                label="จังหวัด/พื้นที่ที่ทำงานได้"
                name="preferredProvinces"
                span="full"
                hint="เลือกได้หลายจังหวัด"
                missing={missingFieldKeys.has("preferredProvinces")}
              >
                <ProvincePicker
                  value={form.preferredProvinces}
                  options={options.provinces}
                  disabled={!canEdit}
                  onChange={(next) => setField("preferredProvinces", next)}
                />
              </Field>

              <FieldGroup Icon={Wallet}>ค่าจ้างที่คาดหวัง</FieldGroup>
              <Field label="เงินเดือนคาดหวัง">
                <input type="number" min={0} {...inputProps("expectedSalaryAmount")} />
              </Field>
              <Field label="สกุลเงิน" hint="เช่น THB">
                <input type="text" maxLength={10} list="wx-wdp-currencies" placeholder="THB" {...inputProps("expectedSalaryCurrency")} />
              </Field>
              <Field label="รอบการจ่าย">
                <select {...inputProps("expectedSalaryPeriod")}>
                  <option value="">ยังไม่ระบุ</option>
                  {(options.salaryPeriods ?? []).map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <RepeatingSection
              name="languages"
              title="ทักษะภาษา"
              addLabel="เพิ่มภาษา"
              rows={rows.languages}
              canEdit={canEdit}
              onAdd={() => addRow("languages")}
              onRemove={(index) => removeRow("languages", index)}
              emptyText="ยังไม่มีข้อมูลภาษา"
              missing={missingFieldKeys.has("languages")}
            >
              {(row, index) => (
                <>
                  <Field label="ภาษา">
                    <input type="text" maxLength={120} {...rowProps("languages", index, "languageName")} />
                  </Field>
                  {[
                    ["listeningLevel", "การฟัง"],
                    ["speakingLevel", "การพูด"],
                    ["readingLevel", "การอ่าน"],
                    ["writingLevel", "การเขียน"],
                  ].map(([field, label]) => (
                    <Field key={field} label={label}>
                      <select {...rowProps("languages", index, field)}>
                        <option value="">ยังไม่ระบุ</option>
                        {(options.languageLevels ?? []).map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </>
              )}
            </RepeatingSection>

            <RepeatingSection
              name="skillItems"
              title="ทักษะและความสามารถ"
              addLabel="เพิ่มทักษะ"
              rows={rows.skillItems}
              canEdit={canEdit}
              onAdd={() => addRow("skillItems")}
              onRemove={(index) => removeRow("skillItems", index)}
              emptyText="ยังไม่มีข้อมูลทักษะ"
            >
              {(row, index) => (
                <>
                  <Field label="ชื่อทักษะ">
                    <input type="text" maxLength={200} {...rowProps("skillItems", index, "skillName")} />
                  </Field>
                  <Field label="ประสบการณ์ (ปี)">
                    <input type="number" min={0} max={60} {...rowProps("skillItems", index, "experienceYears")} />
                  </Field>
                </>
              )}
            </RepeatingSection>

            <RepeatingSection
              name="educations"
              title="ประวัติการศึกษา"
              addLabel="เพิ่มการศึกษา"
              rows={rows.educations}
              canEdit={canEdit}
              onAdd={() => addRow("educations")}
              onRemove={(index) => removeRow("educations", index)}
              emptyText="ยังไม่มีข้อมูลการศึกษา"
              missing={missingFieldKeys.has("educations")}
            >
              {(row, index) => (
                <>
                  <Field label="สถาบันการศึกษา">
                    <input type="text" maxLength={200} {...rowProps("educations", index, "institutionName")} />
                  </Field>
                  <Field label="ระดับการศึกษา">
                    <input type="text" maxLength={120} {...rowProps("educations", index, "educationLevelText")} />
                  </Field>
                  <Field label="สาขาวิชา">
                    <input type="text" maxLength={200} {...rowProps("educations", index, "major")} />
                  </Field>
                  <Field label="ปีเริ่ม">
                    <YearSelect {...rowProps("educations", index, "startYear")} />
                  </Field>
                  <Field label="ปีจบ">
                    <YearSelect {...rowProps("educations", index, "endYear")} />
                  </Field>
                  <Field label="เกรดเฉลี่ย">
                    <input type="text" maxLength={10} {...rowProps("educations", index, "gpaText")} />
                  </Field>
                </>
              )}
            </RepeatingSection>

            <RepeatingSection
              name="experiences"
              title="ประสบการณ์ทำงาน"
              addLabel="เพิ่มประสบการณ์"
              rows={rows.experiences}
              canEdit={canEdit}
              onAdd={() => addRow("experiences")}
              onRemove={(index) => removeRow("experiences", index)}
              emptyText="ยังไม่มีประสบการณ์ทำงาน"
              missing={missingFieldKeys.has("experiences")}
            >
              {(row, index) => (
                <>
                  <Field label="บริษัท / นายจ้าง">
                    <input type="text" maxLength={200} {...rowProps("experiences", index, "companyName")} />
                  </Field>
                  <Field label="ตำแหน่งงาน">
                    <input type="text" maxLength={200} {...rowProps("experiences", index, "jobTitle")} />
                  </Field>
                  <Field label="ประเทศ">
                    <input type="text" maxLength={120} {...rowProps("experiences", index, "countryName")} />
                  </Field>
                  <Field label="ปีเริ่ม">
                    <YearSelect {...rowProps("experiences", index, "startYear")} />
                  </Field>
                  <Field label="ปีสิ้นสุด">
                    <YearSelect {...rowProps("experiences", index, "endYear")} />
                  </Field>
                  <Field label="เงินเดือน" hint="บันทึกตามที่ได้รับจริง เช่น 15,000 บาท">
                    <input type="text" maxLength={120} {...rowProps("experiences", index, "salaryText")} />
                  </Field>
                  <Field label="หมายเหตุ">
                    <input type="text" maxLength={500} {...rowProps("experiences", index, "remark")} />
                  </Field>
                </>
              )}
            </RepeatingSection>

            <RepeatingSection
              name="visas"
              title="วีซ่าและใบอนุญาตทำงาน"
              addLabel="เพิ่มวีซ่า"
              rows={rows.visas}
              canEdit={canEdit}
              onAdd={() => addRow("visas")}
              onRemove={(index) => removeRow("visas", index)}
              emptyText="ยังไม่มีข้อมูลวีซ่า"
            >
              {(row, index) => (
                <>
                  <Field label="ประเภทวีซ่า">
                    <input type="text" maxLength={120} {...rowProps("visas", index, "visaType")} />
                  </Field>
                  <Field label="ประเทศที่ออก">
                    <input type="text" maxLength={120} {...rowProps("visas", index, "issuingCountry")} />
                  </Field>
                  <Field label="เลขที่วีซ่า">
                    <input type="text" maxLength={80} {...rowProps("visas", index, "visaNumber")} />
                  </Field>
                  <Field label="วันที่ออก">
                    <input type="date" {...rowProps("visas", index, "issueDate")} />
                  </Field>
                  <Field label="วันหมดอายุ">
                    <input type="date" {...rowProps("visas", index, "expiryDate")} />
                  </Field>
                </>
              )}
            </RepeatingSection>

            <WorkSamples
              applicationId={applicationId}
              workSamples={profile?.workSamples}
              canEdit={canEdit}
              onChanged={applyProfile}
            />
          </section>
          )}

          {activeSection === "Notes" && (
          <section
            className="wx-wdp-section"
            id="wx-wdp-panel-Notes"
            role="tabpanel"
            aria-labelledby="wx-wdp-tab-Notes"
          >
            <div className="wx-wdp-grid">
              <Field
                label="หมายเหตุจาก Agent/เจ้าหน้าที่"
                span="wide"
                hint="บันทึกภายใน ใช้ประกอบการพิจารณาจับคู่งาน"
              >
                <textarea rows={5} maxLength={1000} {...inputProps("backofficeNotes")} />
              </Field>
            </div>
            <div className="wx-wdp-audit">
              {profile?.audit?.updatedAtUtc ? (
                <p>
                  แก้ไขล่าสุดโดย{" "}
                  <strong>{profile.audit.updatedByDisplayName || profile.audit.updatedByUserId || "ไม่ทราบผู้แก้ไข"}</strong>
                  {profile.audit.updatedByRole ? ` (${profile.audit.updatedByRole})` : ""} เมื่อ{" "}
                  {formatDateTimeTh(profile.audit.updatedAtUtc)}
                </p>
              ) : (
                <p>ยังไม่มีการแก้ไขข้อมูลโปรไฟล์นี้</p>
              )}
            </div>
          </section>
          )}
        </div>
      </div>

      {/* P157-R4: closing this dialog used to discard an unsaved form silently -
          a stray backdrop click after ten minutes of typing lost all of it. The
          host now asks first, and asks HERE rather than in a second modal over
          the first one: the answer belongs beside the Save button the operator
          is already looking at. */}
      <footer className={`wx-wdp-footer${confirmingClose ? " wx-wdp-footer--confirm" : ""}`}>
        {confirmingClose ? (
          <>
            <p className="wx-wdp-confirm-text" role="alert">
              ยังมีการแก้ไขที่ไม่ได้บันทึก ถ้าปิดตอนนี้ข้อมูลที่แก้ไว้จะหายไป
            </p>
            <div className="wx-wdp-footer-actions">
              <button type="button" className="wx-button wx-button--ghost" onClick={() => onDiscardClose?.()}>
                ปิดโดยไม่บันทึก
              </button>
              <button type="button" className="wx-button wx-button--primary" onClick={() => onCancelClose?.()}>
                กลับไปแก้ไขต่อ
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="wx-wdp-footer-status" role="status">
              {saveState.status === "saved" && (
                <span className="wx-wdp-saved">
                  <Check size={15} aria-hidden="true" />
                  {saveState.message}
                </span>
              )}
              {saveState.status === "error" && <span className="wx-wdp-error">{saveState.message}</span>}
              {saveState.status === "idle" && dirty && canEdit && (
                <span className="wx-wdp-dirty">มีการแก้ไขที่ยังไม่ได้บันทึก</span>
              )}
            </div>
            <div className="wx-wdp-footer-actions">
              <button type="button" className="wx-button wx-button--ghost" onClick={handleCloseRequest}>
                ปิด
              </button>
              <button
                type="button"
                className="wx-button wx-button--primary wx-wdp-save"
                onClick={handleSave}
                disabled={!canEdit || saveState.status === "saving"}
              >
                <Save size={16} aria-hidden="true" />
                {saveState.status === "saving" ? "กำลังบันทึก..." : "บันทึกข้อมูล"}
              </button>
            </div>
          </>
        )}
      </footer>
      </div>
    </div>
  );
}

export default WorkerDetailedProfileEditor;
